import type { FlexLayoutDirection } from '../../types.js'
import { FlexLayoutDirectionOptions } from '../../types.js'
import { type ScreenSize } from '../../consts.js'
import { ScreenSizes } from '../../consts.js'
import type { EventType } from '../../types.js'
import type { LayoutAlignmentOption } from '../../types.js'
import { LayoutAlignmentOptions } from '../../types.js'
import { Block } from '../block.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { LogicBlock } from '../logic/logic-block.js'
import { Expression } from '../../data/text-or-code.js'
import { TextOrCode_assumeExpression } from '../../data/text-or-code.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'

@jsonClass('LayoutBlock')
export class LayoutBlock extends Block {
	@jsonProperty(String)
	@ScreenEditorInput<LayoutBlock>({
		inputType: 'dropdown',
		order: 1,
		label: 'Layout',
		options: Object.entries(FlexLayoutDirectionOptions),
		quickActions: [
			{
				name: 'h',
				shortcutKey: 'h',
				action: (blocks) =>
					blocks.forEach((b) => (b.layoutDirection = 'horizontal')),
			},
			{
				name: 'v',
				shortcutKey: 'v',
				action: (blocks) =>
					blocks.forEach((b) => (b.layoutDirection = 'vertical')),
			},
			{
				name: 'g-h',
				shortcutKey: 'H',
				action: (blocks) =>
					blocks.forEach((b) => (b.layoutDirection = 'grid-horizontal')),
			},
			{
				name: 'g-v',
				shortcutKey: 'V',
				action: (blocks) =>
					blocks.forEach((b) => (b.layoutDirection = 'grid-vertical')),
			},
		],
	})
	layoutDirection: FlexLayoutDirection = 'horizontal'

	@jsonProperty(String)
	@ScreenEditorInput<LayoutBlock>({
		inputType: 'dropdown',
		order: 2,
		label: 'Content alignment',
		options: Object.entries(LayoutAlignmentOptions),
		quickActions: [
			{
				name: 'bl',
				action: (blocks) =>
					blocks.forEach((b) => (b.itemAlignment = 'baseline')),
			},
		],
	})
	itemAlignment: LayoutAlignmentOption = 'inherit'

	@jsonProperty(String)
	@ScreenEditorInput<LayoutBlock>({
		inputType: 'dropdown',
		order: 3,
		label: 'Vertical if screen is smaller than',
		tooltip: () => ({ text: 'Use XSmall if you never want to go vertical' }),
		options: ScreenSizes.map((s) => [s, s]),
		isVisible(blocks, properties, bo) {
			return blocks[0].showVerticalIfScreenSmallerThan()
		},
		leaveGapIfHidden: () => true,
	})
	verticalIfScreenSmallerThan: ScreenSize = 'XSmall'

	@ScreenEditorInput<LayoutBlock>({
		inputType: 'code',
		order: 4,
		label: (blocks) =>
			blocks[0].layoutDirection == 'grid-horizontal'
				? 'Number of columns'
				: 'Number of rows',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isVisible(blocks, properties, bo) {
			return (
				blocks[0].layoutDirection == 'grid-horizontal' ||
				blocks[0].layoutDirection == 'grid-vertical'
			)
		},
	})
	@jsonProperty(Expression)
	gridColumnCountCode = new Expression('2')

	@ScreenEditorInput<LayoutBlock>({
		inputType: 'input',
		order: 5,
		label: 'Number of columns for preview',
		contentType: 'number',
		elementType: 'input',
		setter: (target, key, value) =>
			Reflect.set(
				target,
				key,
				Number.isNaN(Number(value)) ? undefined : Number(value),
			),
		isVisible(blocks, properties, bo) {
			return (
				blocks[0].layoutDirection == 'grid-horizontal' ||
				blocks[0].layoutDirection == 'grid-vertical'
			)
		},
	})
	@jsonProperty(Number)
	columnCountPreview?: number

	@ScreenEditorInput<LayoutBlock>({
		inputType: 'code',
		order: 6,
		label: 'Gap between children',
		tooltip: () => ({
			text: 'Specify gap in CSS units (e.g. 1em, 24px, 5%, etc.). If one value is given, it will be applied both vertically and horizontally; provide vertical and horizontal values separately for more control (e.g. 0.5em 1em)',
		}),
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
		quickActions: [
			{
				name: '0',
				shortcutKey: '0',
				action: (blocks) =>
					blocks.forEach(
						(b) => (b.gapCode = new TextOrCode_assumeExpression('0', 'T')),
					),
			},
			{
				name: '0.25em',
				shortcutKey: '2',
				action: (blocks) =>
					blocks.forEach(
						(b) => (b.gapCode = new TextOrCode_assumeExpression('0.25em', 'T')),
					),
			},
			{
				name: '0.5em',
				shortcutKey: '5',
				action: (blocks) =>
					blocks.forEach(
						(b) => (b.gapCode = new TextOrCode_assumeExpression('0.5em', 'T')),
					),
			},
			{
				name: '1em',
				shortcutKey: '1',
				action: (blocks) =>
					blocks.forEach(
						(b) => (b.gapCode = new TextOrCode_assumeExpression('1em', 'T')),
					),
			},
		],
	})
	@jsonProperty(TextOrExpression_assumeExpression)
	gapCode = new TextOrExpression_assumeExpression('1em', 'T')

	@ScreenEditorInput<LayoutBlock>({
		inputType: 'code',
		order: 10,
		label: 'Row definitions',
		tab: 'Advanced',
		tooltip: () => ({
			text: 'Specify exact CSS row definitions if desired.<br>E.g. <pre>auto 1fr 2fr</pre> for one auto-sized row and two additional rows that split the size 1:2. Leave empty for auto-sized grid',
			acceptHtml: true,
		}),
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
		isVisible(blocks, properties, bo) {
			return (
				blocks[0].layoutDirection == 'grid-horizontal' ||
				blocks[0].layoutDirection == 'grid-vertical'
			)
		},
	})
	@jsonProperty(String)
	gridRowDefinitionCode: string = ''

	@ScreenEditorInput<LayoutBlock>({
		inputType: 'code',
		order: 10,
		label: 'Column definitions',
		tab: 'Advanced',
		tooltip: () => ({
			text: 'Specify exact CSS column definitions if desired.<br>E.g. <pre>auto 1fr 2fr</pre> for one auto-sized column and two additional columns that split the size 1:2. Leave empty for auto-sized grid',
			acceptHtml: true,
		}),
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
		isVisible(blocks, properties, bo) {
			return (
				blocks[0].layoutDirection == 'grid-horizontal' ||
				blocks[0].layoutDirection == 'grid-vertical'
			)
		},
	})
	@jsonProperty(String)
	gridColumnDefinitionCode: string = ''

	constructor(init: Partial<LayoutBlock> = {}) {
		super()
		this.init(init)
	}

	isLayoutBlock(): this is LayoutBlock {
		return true
	}

	getEditorTitle() {
		return `${super.getEditorTitle()} ${
			FlexLayoutDirectionOptions[this.layoutDirection]
		}`
	}

	getPreviewDirection(): 'row' | 'column' {
		if (
			this.layoutDirection == 'horizontal' ||
			this.layoutDirection == 'horizontal-reverse' ||
			this.layoutDirection == 'grid-horizontal' ||
			this.layoutDirection == 'grid-vertical'
		) {
			return 'row'
		}
		return 'column'
	}

	getPreviewStyles(): string {
		if (this.layoutDirection == 'grid-horizontal') {
			return `display: grid; grid-template-columns: repeat(${this.columnCountPreview || 2}, auto);`
		}
		if (this.layoutDirection == 'grid-vertical') {
			return `display: grid; grid-template-rows: repeat(${this.columnCountPreview || 2}, auto); grid-auto-flow: column;`
		}
		return super.getPreviewStyles()
	}

	getTitleBackgroundAndBorderColor(): string {
		return '#b0bec5'
	}

	getTitleColor(): string {
		return '#263238'
	}

	getEditorCategory(): string {
		return 'Layout'
	}

	showVerticalIfScreenSmallerThan() {
		return (
			this.layoutDirection == 'horizontal' ||
			this.layoutDirection == 'horizontal-reverse' ||
			this.layoutDirection == 'grid-horizontal'
		)
	}

	get allowedEventTypes(): EventType[] {
		return [
			{ name: 'click' },
			// { name: 'clickOutside' },
		]
	}
}
